import { Animation } from './Animation';
import { Vector3, Quaternion } from '../types';

export class RotationAnimation extends Animation {
    // Rotation-specific properties
    private _baseRotation: Quaternion = Quaternion.identity();
    private _hasStoredBaseRotation: boolean = false;
    
    // --- Manual Overrides ---
    private _manualSpeed: number | null = null;
    
    // Rotation axes (can be customized)
    public rotateX: boolean = false;
    public rotateY: boolean = true; // Default Y-axis rotation (most common)
    public rotateZ: boolean = false;
    
    // Rotation directions (1 = positive, -1 = negative)
    public xDirection: number = 1;
    public yDirection: number = 1;
    public zDirection: number = 1;
    
    // Rotation speed multipliers (relative to base rotationSpeed)
    public xSpeedMultiplier: number = 1.0;
    public ySpeedMultiplier: number = 1.0;
    public zSpeedMultiplier: number = 1.0;

    constructor(name: string = 'Rotation Animation') {
        super(name, 'rotation');
    }

    // Store the original rotation when animation starts
    protected onStart(): void {
        super.onStart();
        
        const target = this.getTarget();
        if (target && target.transform && !this._hasStoredBaseRotation) {
            // Store the current rotation as base rotation
            this._baseRotation = target.transform.rotation.value?.clone() || Quaternion.identity();
            this._hasStoredBaseRotation = true;
        }
    }

    protected onStop(): void {
        super.onStop();
        
        // Restore original rotation when stopping
        const target = this.getTarget();
        if (target && target.transform && this._hasStoredBaseRotation) {
            target.transform.rotation.setImmediate(this._baseRotation);
        }
    }

    protected onReset(): void {
        this._hasStoredBaseRotation = false;
        this._manualSpeed = null;
    }

    // Main animation logic
    protected animate(elapsedTime: number, deltaTime: number): void {
        const target = this.getTarget();
        if (!target || !target.transform) return;
        const transform = target.transform;
        
        // Get live parameters from POI data, with manual override
        const rotationSpeed = this.getRotationSpeed();
        
        // Skip animation if speed is zero
        if (rotationSpeed <= 0) return;
        
        // Calculate rotation based on elapsed time
        const rotationQuaternion = this.calculateRotation(elapsedTime, rotationSpeed);
        
        // Apply rotation to transform
        const newRotation = this._baseRotation.multiply(rotationQuaternion);
        transform.rotation.setImmediate(newRotation);
    }

    public setRotationSpeed(speed: number): void {
        this._manualSpeed = speed;
    }

    public getRotationSpeed(): number {
        return this._manualSpeed ?? 1;
    }

    private calculateRotation(time: number, speed: number): Quaternion {
        let rotationQuat = Quaternion.identity();
        
        if (this.rotateX) {
            const angleX = time * speed * this.xSpeedMultiplier * this.xDirection;
            const xRotation = Quaternion.fromAxisAngle(Vector3.right(), angleX);
            rotationQuat = rotationQuat.multiply(xRotation);
        }
        
        if (this.rotateY) {
            const angleY = time * speed * this.ySpeedMultiplier * this.yDirection;
            const yRotation = Quaternion.fromAxisAngle(Vector3.up(), angleY);
            rotationQuat = rotationQuat.multiply(yRotation);
        }
        
        if (this.rotateZ) {
            const angleZ = time * speed * this.zSpeedMultiplier * this.zDirection;
            const zRotation = Quaternion.fromAxisAngle(Vector3.forward(), angleZ);
            rotationQuat = rotationQuat.multiply(zRotation);
        }
        
        return rotationQuat;
    }

    // Rotation never really "completes" - it's continuous
    protected shouldCompleteLoop(elapsedTime: number): boolean {
        return false; // Continuous animation
    }

    // Rotation pattern presets
    setRotationPattern(pattern: RotationPattern): void {
        switch (pattern) {
            case 'yaw':
                this.rotateX = false;
                this.rotateY = true;
                this.rotateZ = false;
                this.yDirection = 1;
                this.ySpeedMultiplier = 1.0;
                break;
                
            case 'pitch':
                this.rotateX = true;
                this.rotateY = false;
                this.rotateZ = false;
                this.xDirection = 1;
                this.xSpeedMultiplier = 1.0;
                break;
                
            case 'roll':
                this.rotateX = false;
                this.rotateY = false;
                this.rotateZ = true;
                this.zDirection = 1;
                this.zSpeedMultiplier = 1.0;
                break;
                
            case 'tumble':
                this.rotateX = true;
                this.rotateY = true;
                this.rotateZ = true;
                this.xDirection = 1;
                this.yDirection = 1;
                this.zDirection = 1;
                this.xSpeedMultiplier = 0.7;
                this.ySpeedMultiplier = 1.0;
                this.zSpeedMultiplier = 0.5;
                break;
                
            case 'wobble':
                this.rotateX = true;
                this.rotateY = false;
                this.rotateZ = true;
                this.xDirection = 1;
                this.zDirection = -1;
                this.xSpeedMultiplier = 1.0;
                this.zSpeedMultiplier = 0.8;
                break;
                
            case 'spin':
                this.rotateX = false;
                this.rotateY = true;
                this.rotateZ = false;
                this.yDirection = 1;
                this.ySpeedMultiplier = 2.0; // Faster spin
                break;
        }
    }

    // Set rotation axis
    setRotationAxis(axis: Vector3, direction: number = 1): void {
        // Reset all axes
        this.rotateX = false;
        this.rotateY = false;
        this.rotateZ = false;
        
        // Set the dominant axis
        const absAxis = new Vector3(Math.abs(axis.x), Math.abs(axis.y), Math.abs(axis.z));
        
        if (absAxis.x >= absAxis.y && absAxis.x >= absAxis.z) {
            this.rotateX = true;
            this.xDirection = Math.sign(axis.x) * direction;
        } else if (absAxis.y >= absAxis.x && absAxis.y >= absAxis.z) {
            this.rotateY = true;
            this.yDirection = Math.sign(axis.y) * direction;
        } else {
            this.rotateZ = true;
            this.zDirection = Math.sign(axis.z) * direction;
        }
    }

    // Reset base rotation (useful when POI is rotated)
    resetBaseRotation(): void {
        const target = this.getTarget();
        if (target && target.transform) {
            this._baseRotation = target.transform.rotation.value?.clone() || Quaternion.identity();
            this._hasStoredBaseRotation = true;
        }
    }

    // Get current rotation offset (useful for debugging)
    getCurrentRotationOffset(): Quaternion {
        if (!this.isRunning()) return Quaternion.identity();
        
        const elapsedTime = this.getElapsedTimeSeconds();
        const rotationSpeed = this.getRotationSpeed();
        
        return this.calculateRotation(elapsedTime, rotationSpeed);
    }

    // Get current total rotation
    getCurrentRotation(): Quaternion {
        if (!this.isRunning()) return this._baseRotation;
        
        const rotationOffset = this.getCurrentRotationOffset();
        return this._baseRotation.multiply(rotationOffset);
    }


}

// Rotation pattern types
export type RotationPattern = 'yaw' | 'pitch' | 'roll' | 'tumble' | 'wobble' | 'spin'; 